import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * The trip being composed, held across the confirm and searching steps.
 *
 * Mirrors the app's `TripRequestContext`. Deliberately in memory only: a
 * half-finished booking restored from storage days later would quote a stale
 * fare against a route the customer has forgotten choosing, and the app does
 * not persist it either.
 */

export interface BookingDraftValue {
  pickup: { lat: number; lng: number; address: string } | null;
  dropoff: { lat: number; lng: number; address: string } | null;
  vehicleType: string | null;
  estimatedFare: number | null;
  distanceKm: number | null;
  notes: string;
  /**
   * One key per booking attempt, reused across retries.
   *
   * The backend collapses retries that share a key onto a single trip. A fresh
   * key on retry means a second trip and a second card authorisation, so this
   * is created once when the draft is started and never regenerated on error.
   */
  idempotencyKey: string;
}

interface BookingDraftContext {
  draft: BookingDraftValue;
  setDraft: (patch: Partial<Omit<BookingDraftValue, 'idempotencyKey'>>) => void;
  /** Clears everything and mints a NEW idempotency key for the next attempt. */
  reset: () => void;
  isComplete: boolean;
}

function newIdempotencyKey(): string {
  const cryptoApi = (globalThis as { crypto?: Crypto }).crypto;

  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();

  /* jsdom and older browsers lack randomUUID. Uniqueness per attempt is what
     matters here, not cryptographic quality. */
  return `sw-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

const EMPTY: Omit<BookingDraftValue, 'idempotencyKey'> = {
  pickup: null,
  dropoff: null,
  vehicleType: null,
  estimatedFare: null,
  distanceKm: null,
  notes: '',
};

const Context = createContext<BookingDraftContext | undefined>(undefined);

export const BookingDraftProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [draft, setDraftState] = useState<BookingDraftValue>({
    ...EMPTY,
    idempotencyKey: newIdempotencyKey(),
  });

  const setDraft = useCallback((patch: Partial<Omit<BookingDraftValue, 'idempotencyKey'>>) => {
    setDraftState((previous) => ({ ...previous, ...patch }));
  }, []);

  const reset = useCallback(() => {
    setDraftState({ ...EMPTY, idempotencyKey: newIdempotencyKey() });
  }, []);

  const value = useMemo<BookingDraftContext>(
    () => ({
      draft,
      setDraft,
      reset,
      isComplete: Boolean(
        draft.pickup && draft.dropoff && draft.vehicleType && draft.estimatedFare != null,
      ),
    }),
    [draft, setDraft, reset],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export function useBookingDraft(): BookingDraftContext {
  const context = useContext(Context);
  if (!context) throw new Error('useBookingDraft must be used inside BookingDraftProvider');
  return context;
}
